import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  CircularProgress,
  Divider,
  Button,
  Avatar // <-- NUEVA IMPORTACIÓN
} from '@mui/material';

// --- NUEVOS ICONOS ---
import GroupIcon from '@mui/icons-material/Group';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
// ----------------------

// Si usas react-router-dom:
import { Link } from 'react-router-dom'; 

// Hooks de RTK Query
import { 
  useGetVendidoPorVendedorQuery, 
  useGetFacturadoByProvinciaQuery,
  useGetClientesVigentesQuery,
  useGetClientesVencidosQuery
} from '../../store/api/endpoints/reportesApiSlice';
import { SelectVendedor } from '../utils/SelectUsuario';
import { SelectRangoFechas } from '../utils/SelectRangoFechas';

// Función helper rápida para no depender de Day.js en el estado local
const getFechasIniciales = () => {
  const hoy = new Date();
  const yyyy = hoy.getFullYear();
  const mm = String(hoy.getMonth() + 1).padStart(2, '0');
  const dd = String(hoy.getDate()).padStart(2, '0');
  
  return {
    primerDiaMes: `${yyyy}-${mm}-01`,
    hoy: `${yyyy}-${mm}-${dd}`
  };
};

// Formateador de moneda
const formatCurrency = (value) => {
  if (value == null) return '0,00';
  return value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Diccionario para renombrar las keys de descuentos del JSON a nombres legibles
const NOMBRES_DESCUENTOS = {
  mercadopago: 'Mercado Pago',
  iva_afip: 'IVA AFIP',
  iibb: 'Ingresos Brutos (IIBB)',
  ganancias: 'Ganancias'
};

export default function Home() {
  const fechas = getFechasIniciales();

  // 1. Estado local
  const [vendedorId, setVendedorId] = useState("0"); 
  const [rangoFechas, setRangoFechas] = useState({
    fechaDesde: fechas.primerDiaMes, 
    fechaHasta: fechas.hoy,          
  });

  // 2. Parámetros para RTK Query
  const queryParams = {
    vendedorId,
    fechaDesde: rangoFechas.fechaDesde || '',
    fechaHasta: rangoFechas.fechaHasta || '',
  };

  // 3. Consumo de Endpoints
  const { 
    data: dataVendedor, 
    isLoading: loadingVendedor, 
    isFetching: fetchingVendedor 
  } = useGetVendidoPorVendedorQuery(queryParams);

  const { 
    data: dataProvincia, 
    isLoading: loadingProvincia, 
    isFetching: fetchingProvincia 
  } = useGetFacturadoByProvinciaQuery(queryParams);

  // --- NUEVOS ENDPOINTS (Indicadores) ---
  const { data: dataVigentes, isLoading: loadingVigentes } = useGetClientesVigentesQuery({ vendedorId });
  const { data: dataVencidos, isLoading: loadingVencidos } = useGetClientesVencidosQuery({ vendedorId });
  // --------------------------------------

  const handleRangoChange = (nuevoRango) => {
    setRangoFechas(nuevoRango);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, margin: '0 auto' }}>
      
      <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 2.5 }}>
        Reportes Generales
      </Typography>

      {/* Sección de Filtros (Más compacta) */}
      <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', flexDirection: 'column', gap: 2, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Filtros de búsqueda
        </Typography>
        <Box 
          sx={{ 
            display: 'flex', 
            gap: 2, 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center'
          }}
        >
          <Box sx={{ width: { xs: '100%', sm: '35%' } }}>
            <SelectVendedor value={vendedorId} onChange={setVendedorId} />
          </Box>
          
          <Box sx={{ width: { xs: '100%', sm: '65%' } }}>
            <SelectRangoFechas 
              fechaDesde={rangoFechas.fechaDesde} 
              fechaHasta={rangoFechas.fechaHasta} 
              onChange={handleRangoChange} 
            />
          </Box>
        </Box>
      </Paper>

      {/* NUEVA SECCIÓN KPI COMPACTA */}
      <Box 
        sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
          gap: 3, 
          mb: 3 
        }}
      >
        {/* KPI Clientes Vigentes (Compacto) */}
        <Paper 
          elevation={0} 
          variant="outlined"
          sx={{ 
            p: 2, 
            borderColor: 'divider',
            borderTop: '2px solid',
            borderTopColor: 'success.main', // Acento sutil superior
            display: 'flex',
            alignItems: 'center', // Distribución horizontal
            gap: 2.5,
          }}
        >
          <Avatar 
            sx={{ 
              bgcolor: '#ccc !important', // Fondo suave del color semántico
              color: 'success.main',
              width: 50,
              height: 50,
            }}
          >
            <GroupIcon fontSize="medium" />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Clientes Vigentes
            </Typography>
            {loadingVigentes ? (
              <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}><CircularProgress size={20} color="success" /></Box>
            ) : (
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.3 }}>
                {dataVigentes?.cantidad_vigentes ?? 0}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* KPI Clientes Vencidos (Compacto con Botón) */}
        <Paper 
          elevation={0} 
          variant="outlined"
          sx={{ 
            p: 2, 
            borderColor: 'divider',
            borderTop: '2px solid',
            borderTopColor: 'error.main', // Acento sutil superior
            display: 'flex',
            alignItems: 'center', // Distribución horizontal
            gap: 2.5,
          }}
        >
          <Avatar 
            sx={{ 
              bgcolor: '#ccc !important', // Fondo suave del color semántico
              color: 'error.main',
              width: 50,
              height: 50,
            }}
          >
            <ReportProblemIcon fontSize="medium" />
          </Avatar>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Clientes Vencidos
            </Typography>
            {loadingVencidos ? (
              <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}><CircularProgress size={20} color="error" /></Box>
            ) : (
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.3 }}>
                {dataVencidos?.cantidad_vencidos ?? 0}
              </Typography>
            )}
          </Box>
          
          {/* Botón Ver Detalle Compacto */}
          <Button
            component={Link}
            to={`/home/clientes-vencidos?vendedorId=${vendedorId}`}
            variant="text"
            color="error"
            size="small"
            disabled={loadingVencidos || !dataVencidos?.cantidad_vencidos}
            sx={{ 
              alignSelf: 'center', // Centrado verticalmente respecto a la fila
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '0.75rem',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.04)'
              }
            }}
          >
            Ver Detalle
          </Button>
        </Paper>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Contenedor de Tablas */}
      <Box 
        sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, 
          gap: 3, // Reducido ligeramente el gap
          alignItems: 'start'
        }}
      >
        
        {/* TABLA 1: Resumen Vertical por Vendedor */}
        <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ position: 'relative', borderColor: 'divider' }}>
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
              Resumen Financiero de Ventas
            </Typography>
            {(loadingVendedor || fetchingVendedor) && <CircularProgress size={18} sx={{ ml: 2 }} />}
          </Box>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'action.hover' }}>
              <TableRow>
                <TableCell><Typography variant="body2" sx={{fontWeight: 'bold'}}>Concepto / Detalle</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" sx={{fontWeight: 'bold'}}>Valor</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingVendedor ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell>
                </TableRow>
              ) : dataVendedor ? (
                <>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary' }}>Período Reportado</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                      {dataVendedor.fechaDesde} al {dataVendedor.fechaHasta}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'medium' }}>Facturación Bruta</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                      ${formatCurrency(dataVendedor.facturacion_bruta)}
                    </TableCell>
                  </TableRow>
                  {dataVendedor.descuentos && Object.entries(dataVendedor.descuentos).map(([key, value]) => (
                    <TableRow key={key} sx={{ '& td': { borderBottom: 'none' } }}>
                      <TableCell sx={{ pl: 4, color: 'text.secondary', fontSize: '0.8rem' }}>
                        {NOMBRES_DESCUENTOS[key] || key}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        -${formatCurrency(value)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'rgba(211, 47, 47, 0.01)' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'error.main' }}>(-) Total Descuentos</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      -${formatCurrency(dataVendedor.descuentos_totales)}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'rgba(46, 125, 50, 0.01)' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>(=) Total Ganancia Neta</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      ${formatCurrency(dataVendedor.total_ganancia)}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'rgba(2, 136, 209, 0.05)' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'info.main', py: 1.2 }}>Total Comisión a Pagar</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '0.95rem' }}>
                      ${formatCurrency(dataVendedor.total_comision)}
                    </TableCell>
                  </TableRow>
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 3 }}>No hay datos disponibles</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* TABLA 2: Facturado por Provincia */}
        <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ position: 'relative', borderColor: 'divider' }}>
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
              Facturado por Provincia
            </Typography>
            {(loadingProvincia || fetchingProvincia) && <CircularProgress size={18} sx={{ ml: 2 }} />}
          </Box>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'action.hover' }}>
              <TableRow>
                <TableCell><Typography variant="body2" sx={{fontWeight: 'bold'}}>Provincia</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" sx={{fontWeight: 'bold'}}>Total Facturado</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingProvincia ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell>
                </TableRow>
              ) : dataProvincia && dataProvincia.datos && Object.keys(dataProvincia.datos).length > 0 ? (
                Object.entries(dataProvincia.datos).map(([provincia, importe]) => (
                  <TableRow key={provincia}>
                    <TableCell>{provincia}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: '700' }}>
                      ${formatCurrency(importe)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 3 }}>No hay datos disponibles</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

      </Box>
    </Box>
  );
}